import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase.js';
import { toast } from 'react-toastify';

const PROJECT_TYPES = [
  { value: 'deck', label: 'Deck / porch' },
  { value: 'paint', label: 'Exterior paint / stain' },
  { value: 'fence', label: 'Fence / wall' },
  { value: 'roof', label: 'Roofing' },
  { value: 'landscape', label: 'Landscaping / trees' },
  { value: 'shed', label: 'Shed / outbuilding' },
  { value: 'other', label: 'Other exterior change' },
];

const STATUS_LABELS = {
  submitted: 'Submitted',
  under_review: 'Under review',
  approved: 'Approved',
  denied: 'Denied',
  revision_requested: 'Revision requested',
};

const ADMIN_STATUSES = ['under_review', 'approved', 'denied', 'revision_requested'];

function formatArcDate(value) {
  if (!value) return '—';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

async function fetchUserLotIds(user) {
  if (!user) return [];
  const ownersQuery = query(collection(db, 'owners'), where('OwnerId', '==', user.uid));
  const ownersQuery2 = query(collection(db, 'owners'), where('OwnerId2', '==', user.uid));
  const [snapshot1, snapshot2] = await Promise.all([getDocs(ownersQuery), getDocs(ownersQuery2)]);
  const lots = [...snapshot1.docs, ...snapshot2.docs].map((row) => row.id);
  return [...new Set(lots)];
}

function ArcReviewPage({ user, isAdmin, isMobile }) {
  const [requests, setRequests] = useState([]);
  const [userLotIds, setUserLotIds] = useState([]);
  const [ownerDirectory, setOwnerDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [newRequest, setNewRequest] = useState({
    lotId: '',
    projectType: 'deck',
    title: '',
    description: '',
  });
  const [reviewDrafts, setReviewDrafts] = useState({});

  const lotLabelById = useMemo(() => {
    const map = new Map();
    ownerDirectory.forEach((lot) => {
      map.set(lot.lotId, lot.tract ? `Lot ${lot.tract}` : lot.lotId);
    });
    return map;
  }, [ownerDirectory]);

  const ownerLotOptions = useMemo(() => {
    if (isAdmin) return ownerDirectory;
    return ownerDirectory.filter((lot) => userLotIds.includes(lot.lotId));
  }, [isAdmin, ownerDirectory, userLotIds]);

  const loadOwnerDirectory = useCallback(async () => {
    const snapshot = await getDocs(collection(db, 'owners'));
    const lots = snapshot.docs.map((row) => ({
      lotId: row.id,
      tract: row.data().tract || row.id,
      address: row.data().address || '',
    }));
    lots.sort((a, b) => String(a.tract).localeCompare(String(b.tract), undefined, { numeric: true }));
    setOwnerDirectory(lots);
    return lots;
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const lots = isAdmin ? [] : await fetchUserLotIds(user);
      setUserLotIds(lots);

      let rows = [];
      if (isAdmin) {
        const snapshot = await getDocs(query(collection(db, 'arc_requests'), orderBy('createdAt', 'desc')));
        rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
      } else if (lots.length === 0) {
        rows = [];
      } else if (lots.length <= 10) {
        const snapshot = await getDocs(query(collection(db, 'arc_requests'), where('lotId', 'in', lots)));
        rows = snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
      } else {
        const chunks = [];
        for (let i = 0; i < lots.length; i += 10) {
          chunks.push(lots.slice(i, i + 10));
        }
        const snapshots = await Promise.all(
          chunks.map((chunk) => getDocs(query(collection(db, 'arc_requests'), where('lotId', 'in', chunk)))),
        );
        const merged = new Map();
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((row) => merged.set(row.id, { id: row.id, ...row.data() }));
        });
        rows = [...merged.values()].sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
      }

      setRequests(rows);
      const drafts = {};
      rows.forEach((row) => {
        drafts[row.id] = {
          status: row.status || 'submitted',
          adminNotes: row.adminNotes || '',
        };
      });
      setReviewDrafts(drafts);
    } catch (error) {
      console.error('Failed to load ARC requests:', error);
      toast.error(error.message || 'Failed to load ARC requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user]);

  useEffect(() => {
    document.title = 'Architectural Review | Nantahala Mountain Resort';
    return () => {
      document.title = 'NMR Community';
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    loadOwnerDirectory().catch((error) => console.error(error));
    loadRequests();
  }, [user, loadOwnerDirectory, loadRequests]);

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return requests;
    return requests.filter((row) => row.status === statusFilter);
  }, [requests, statusFilter]);

  const uploadPhotos = async (requestId) => {
    if (!photoFiles.length) return [];
    const urls = [];
    for (const file of photoFiles.slice(0, 5)) {
      const storageRef = ref(storage, `arc_attachments/${requestId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      urls.push(await getDownloadURL(storageRef));
    }
    return urls;
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    const lotId = newRequest.lotId.trim();
    const title = newRequest.title.trim();
    const description = newRequest.description.trim();

    if (!lotId || !title || !description) {
      toast.error('Lot, title, and description are required.');
      return;
    }

    if (!isAdmin && !userLotIds.includes(lotId)) {
      toast.error('You can only submit requests for your own lot(s).');
      return;
    }

    setSaving(true);
    try {
      const docRef = await addDoc(collection(db, 'arc_requests'), {
        lotId,
        projectType: newRequest.projectType,
        title,
        description,
        status: 'submitted',
        photoUrls: [],
        adminNotes: '',
        submittedBy: user.uid,
        submittedByEmail: user.email || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const photoUrls = await uploadPhotos(docRef.id);
      if (photoUrls.length > 0) {
        await updateDoc(docRef, { photoUrls, updatedAt: serverTimestamp() });
      }

      toast.success('ARC request submitted.');
      setNewRequest({ lotId: '', projectType: 'deck', title: '', description: '' });
      setPhotoFiles([]);
      setShowCreateForm(false);
      await loadRequests();
    } catch (error) {
      console.error('Create ARC request failed:', error);
      toast.error(error.message || 'Could not submit ARC request');
    } finally {
      setSaving(false);
    }
  };

  const handleReviewSave = async (requestId) => {
    if (!isAdmin) return;
    const draft = reviewDrafts[requestId];
    if (!draft) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, 'arc_requests', requestId), {
        status: draft.status,
        adminNotes: draft.adminNotes.trim(),
        reviewedAt: serverTimestamp(),
        reviewedBy: user.uid,
        updatedAt: serverTimestamp(),
      });
      toast.success('ARC review saved.');
      await loadRequests();
    } catch (error) {
      console.error('ARC review save failed:', error);
      toast.error(error.message || 'Could not save review');
    } finally {
      setSaving(false);
    }
  };

  const projectLabel = (value) => PROJECT_TYPES.find((t) => t.value === value)?.label || value;

  return (
    <div className={`docs-page ${isMobile ? 'docs-page-mobile' : ''}`}>
      <article className="docs-card docs-card-wide">
        <header className="docs-header">
          <h1 className="docs-title">Architectural Review (ARC)</h1>
          <p className="docs-meta">
            {isAdmin
              ? 'Review exterior change requests, approve or deny with notes, and request revisions.'
              : 'Submit exterior modification requests for board architectural review.'}
          </p>
        </header>

        <section className="docs-section">
          <div className="violations-toolbar">
            <label className="violations-filter">
              Status
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className="primary-button"
              onClick={() => setShowCreateForm((open) => !open)}
            >
              {showCreateForm ? 'Cancel' : 'New ARC request'}
            </button>
          </div>

          {!isAdmin && userLotIds.length === 0 && !loading ? (
            <p className="violations-empty">
              No lots are linked to your account. Contact the board before submitting an ARC request.
            </p>
          ) : null}

          {showCreateForm ? (
            <form className="violations-create-form arc-create-form" onSubmit={handleCreate}>
              <h2>Submit architectural review request</h2>
              <label>
                Lot
                <select
                  value={newRequest.lotId}
                  onChange={(e) => setNewRequest((prev) => ({ ...prev, lotId: e.target.value }))}
                  required
                >
                  <option value="">Select lot…</option>
                  {ownerLotOptions.map((lot) => (
                    <option key={lot.lotId} value={lot.lotId}>
                      {lot.tract}
                      {lot.address ? ` — ${lot.address}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Project type
                <select
                  value={newRequest.projectType}
                  onChange={(e) => setNewRequest((prev) => ({ ...prev, projectType: e.target.value }))}
                >
                  {PROJECT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Project title
                <input
                  type="text"
                  value={newRequest.title}
                  onChange={(e) => setNewRequest((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Rear deck expansion"
                  required
                />
              </label>
              <label>
                Description & materials
                <textarea
                  value={newRequest.description}
                  onChange={(e) => setNewRequest((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Scope, dimensions, colors, materials, and contractor (if known)"
                  rows={5}
                  required
                />
              </label>
              <label>
                Photos (optional, up to 5)
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setPhotoFiles(Array.from(e.target.files || []).slice(0, 5))}
                />
              </label>
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Submitting…' : 'Submit request'}
              </button>
            </form>
          ) : null}

          {loading ? <p>Loading ARC requests…</p> : null}

          {!loading && filteredRequests.length === 0 ? (
            <p className="violations-empty">No ARC requests match this filter.</p>
          ) : null}

          <ul className="violations-list arc-list">
            {filteredRequests.map((request) => {
              const status = request.status || 'submitted';
              const draft = reviewDrafts[request.id] || { status, adminNotes: request.adminNotes || '' };
              return (
                <li key={request.id} className={`violations-item arc-item arc-item--${status}`}>
                  <div className="violations-item-header">
                    <div>
                      <h3>{request.title}</h3>
                      <p className="violations-item-meta">
                        {lotLabelById.get(request.lotId) || request.lotId}
                        {' · '}
                        {projectLabel(request.projectType)}
                        {' · '}
                        {STATUS_LABELS[status] || status}
                        {' · '}
                        Submitted {formatArcDate(request.createdAt)}
                      </p>
                    </div>
                    <span className={`violations-status-badge arc-status-badge arc-status-badge--${status}`}>
                      {STATUS_LABELS[status] || status}
                    </span>
                  </div>
                  <p className="violations-item-description">{request.description}</p>

                  {request.photoUrls?.length > 0 ? (
                    <div className="arc-photo-grid">
                      {request.photoUrls.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt="" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  ) : null}

                  {request.adminNotes ? (
                    <div className="arc-admin-notes">
                      <strong>Board notes:</strong> {request.adminNotes}
                    </div>
                  ) : null}

                  {request.reviewedAt ? (
                    <p className="violations-item-meta">
                      Last reviewed {formatArcDate(request.reviewedAt)}
                    </p>
                  ) : null}

                  {isAdmin ? (
                    <div className="arc-review-panel">
                      <label>
                        Status
                        <select
                          value={draft.status}
                          onChange={(e) =>
                            setReviewDrafts((prev) => ({
                              ...prev,
                              [request.id]: { ...draft, status: e.target.value },
                            }))
                          }
                        >
                          <option value="submitted">Submitted</option>
                          {ADMIN_STATUSES.map((value) => (
                            <option key={value} value={value}>{STATUS_LABELS[value]}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Board notes to owner
                        <textarea
                          value={draft.adminNotes}
                          rows={3}
                          placeholder="Approval conditions, denial reason, or revision details"
                          onChange={(e) =>
                            setReviewDrafts((prev) => ({
                              ...prev,
                              [request.id]: { ...draft, adminNotes: e.target.value },
                            }))
                          }
                        />
                      </label>
                      <button
                        type="button"
                        className="secondary-button"
                        disabled={saving}
                        onClick={() => handleReviewSave(request.id)}
                      >
                        Save review
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      </article>
    </div>
  );
}

export default ArcReviewPage;